import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

/**
 * Input component props
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Error state */
  error?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Label for the input */
  label?: string;
  /** Hint text displayed below the input */
  hint?: string;
  /** Decorative icon shown inside the left edge of the field */
  leftIcon?: ReactNode;
}

/**
 * Input - Text input component with error states
 *
 * Features:
 * - Proper focus styles using theme tokens
 * - Error state styling
 * - Optional label
 * - Forwarded ref for form libraries
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error = false, errorMessage, label, hint, leftIcon, className, id, ...props }, ref) => {
    const inputId = id || (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block mb-2 text-sm font-medium text-text-primary"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span
              className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-text-muted"
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full py-3',
              leftIcon ? 'pl-10 pr-4' : 'px-4',
              'font-body text-base text-text-primary',
              'bg-bg-tertiary border rounded',
              'transition-all duration-fast ease-default',
              'placeholder:text-text-muted',
              'hover:border-border-strong',
              'focus:outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-primary/40',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error
                ? 'border-semantic-error focus:border-semantic-error focus:ring-semantic-error/40'
                : 'border-border-default',
              className
            )}
            {...props}
          />
        </div>
        {hint && !error && (
          <p className="mt-1 text-xs text-text-muted">{hint}</p>
        )}
        {error && errorMessage && (
          <p className="mt-1 text-xs text-semantic-error">{errorMessage}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
